import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { Mascot, PageTransition, Spinner } from '@redpandev/ui';
import { Header } from './components/Header';
import { RouteAnnouncer } from './app/RouteAnnouncer';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RequireAuth } from './auth/RequireAuth';
import { usePushRevalidation } from './lib/push/usePushRevalidation';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ProfilePage } from './pages/ProfilePage';
import { PublicProfilePage } from './pages/PublicProfilePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { isPandaPanicEnabled, isPandaPanicOnlineEnabled } from './games/panda-panic/flag';

// Chaque jeu est un chunk chargé à la demande (ADR 0005).
const MotusPage = lazy(() => import('./games/motus'));
const MotusDailyPage = lazy(() => import('./games/motus-daily'));
// Griffonne (dessine & devine) : PUBLIQUE — jouable sans compte (invité, ADR 0013).
const GriffonnePage = lazy(() => import('./games/griffonne'));
const GriffonneJoinByCode = lazy(() => import('./games/griffonne/JoinByCode'));
// Pages emails (spec 06, spec 10 §6) : publiques, atteintes depuis les liens des emails → lazy.
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const EmailUnsubscribePage = lazy(() => import('./pages/EmailUnsubscribePage'));
const EmailUnsubscribeInvitesPage = lazy(() => import('./pages/EmailUnsubscribeInvitesPage'));
// Reset de mot de passe (spec 06 §5.5) : publiques, rarement visitées → lazy.
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
// Connexion sociale (spec 11) : onboarding signup différé + confirmation de liaison.
// Publiques (aucune session), atteintes après un retour de callback OAuth → lazy.
const SocialSignupPage = lazy(() => import('./pages/SocialSignupPage'));
const LinkConfirmPage = lazy(() => import('./pages/LinkConfirmPage'));
// Réglages (spec 08) : rarement visités → un chunk pour toute la zone.
const SettingsArea = lazy(() => import('./pages/settings'));
// Panda Panic (spec 15 §5 + games/panda-panic.md) : préversion 3D + aire EN
// LIGNE (Phase 4, spec §12.4) dans UN SEUL chunk lazy `panda-panic-*` (voir
// games/panda-panic/index.tsx — garde tout le code 3D hors du précache SW). Les
// flags (défaut off → routes ABSENTES, prod intacte) sont lus hors du chunk lazy
// (three.js ne se charge qu'à la visite). `mode` = préversion locale / online.
const PandaPanicArea = lazy(() => import('./games/panda-panic'));
// Pages légales (spec 14) : PUBLIQUES, URLs stables reportées dans la console
// Google OAuth (confidentialité + CGU). Rarement visitées → lazy.
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const LegalNoticePage = lazy(() => import('./pages/legal/LegalNoticePage'));

function LazySplash({ message = 'Chargement…' }: { message?: string }) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
      <div className="motion-safe:animate-float">
        <Mascot mood="excited" size={80} />
      </div>
      <p className="flex items-center gap-3 font-display text-2xl text-ink-100">
        <Spinner size="md" label={message} />
        <span aria-hidden="true">{message}</span>
      </p>
    </div>
  );
}

export default function App() {
  const location = useLocation();
  // Revalidation silencieuse du push au boot (user connecté, permission déjà
  // accordée) — MAJ lastSeenAt + rotation de token absorbée (spec 13 §9).
  usePushRevalidation();
  // La zone /settings a sa propre transition de CONTENU (SettingsLayout) :
  // une seule clé pour toute la zone, sinon sidebar et sous-header
  // re-animeraient à chaque changement de section (spec 08 §4.1).
  const transitionKey = location.pathname.startsWith('/settings') ? '/settings' : location.pathname;

  return (
    <div className="min-h-full">
      {/* Premier arrêt du Tab : sauter les barres pour aller au contenu. */}
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-modal focus:rounded-full focus:bg-panda-500 focus:px-5 focus:py-2.5 focus:font-display focus:font-semibold focus:text-ink-950"
      >
        Aller au contenu
      </a>
      {/* Titre + annonce lecteur d'écran + scroll-top à chaque navigation. */}
      <RouteAnnouncer />
      {/* Navbar persistante, hors transition de page (spec 05 §5.1). */}
      <Header />
      <div id="contenu" tabIndex={-1} className="outline-none">
        {/* Transition d'ENTRÉE seule, rejouée au changement de clé — PAS
            d'AnimatePresence mode="wait" : son exit s'est bloqué deux fois
            (layoutId partagé, puis updates répétées d'un enfant à clé fixe
            depuis /settings) en laissant la page suivante à opacity 0. */}
        <PageTransition key={transitionKey}>
          <Routes location={location}>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route
              path="/profile"
              element={
                <RequireAuth>
                  <ProfilePage />
                </RequireAuth>
              }
            />
            <Route
              path="/settings/*"
              element={
                <RequireAuth>
                  <Suspense fallback={<LazySplash />}>
                    <SettingsArea />
                  </Suspense>
                </RequireAuth>
              }
            />
            {/* Standard W3C : les gestionnaires de mots de passe visitent ce
                  chemin pour proposer « changer le mot de passe » (spec 08 §4.3). */}
            <Route
              path="/.well-known/change-password"
              element={<Navigate to="/settings/security?open=password" replace />}
            />
            <Route path="/u/:displayName" element={<PublicProfilePage />} />
            {/* Pages légales (spec 14) : publiques, URLs stables (console Google/Meta). */}
            <Route
              path="/confidentialite"
              element={
                <Suspense fallback={<LazySplash />}>
                  <PrivacyPage />
                </Suspense>
              }
            />
            <Route
              path="/cgu"
              element={
                <Suspense fallback={<LazySplash />}>
                  <TermsPage />
                </Suspense>
              }
            />
            <Route
              path="/mentions-legales"
              element={
                <Suspense fallback={<LazySplash />}>
                  <LegalNoticePage />
                </Suspense>
              }
            />
            {/* Pages publiques atteintes depuis les emails (spec 06 §7). */}
            <Route
              path="/verify-email"
              element={
                <Suspense fallback={<LazySplash />}>
                  <VerifyEmailPage />
                </Suspense>
              }
            />
            <Route
              path="/email/unsubscribe"
              element={
                <Suspense fallback={<LazySplash />}>
                  <EmailUnsubscribePage />
                </Suspense>
              }
            />
            {/* Suppression des emails d'invitation (spec 10 §6) : publique, fonctionne déconnecté. */}
            <Route
              path="/email/unsubscribe-invites"
              element={
                <Suspense fallback={<LazySplash />}>
                  <EmailUnsubscribeInvitesPage />
                </Suspense>
              }
            />
            {/* Reset de mot de passe (spec 06 §5.5) : demande + lien reçu par email. */}
            <Route
              path="/forgot-password"
              element={
                <Suspense fallback={<LazySplash />}>
                  <ForgotPasswordPage />
                </Suspense>
              }
            />
            <Route
              path="/reset-password"
              element={
                <Suspense fallback={<LazySplash />}>
                  <ResetPasswordPage />
                </Suspense>
              }
            />
            {/* Connexion sociale (spec 11 §5, §4.2 c) : onboarding pseudo & liaison. */}
            <Route
              path="/signup/social"
              element={
                <Suspense fallback={<LazySplash />}>
                  <SocialSignupPage />
                </Suspense>
              }
            />
            <Route
              path="/login/link-confirm"
              element={
                <Suspense fallback={<LazySplash />}>
                  <LinkConfirmPage />
                </Suspense>
              }
            />
            {/* PUBLIQUE (ADR 0012) : le mot du jour est le produit d'appel —
                un visiteur joue sans compte (identité invitée côté serveur). */}
            <Route
              path="/motus-daily"
              element={
                <Suspense fallback={<LazySplash message="Chargement du jeu…" />}>
                  <MotusDailyPage />
                </Suspense>
              }
            />
            <Route
              path="/motus"
              element={
                <RequireAuth>
                  <Suspense fallback={<LazySplash message="Chargement du jeu…" />}>
                    <MotusPage />
                  </Suspense>
                </RequireAuth>
              }
            />
            <Route
              path="/motus/m/:matchId"
              element={
                <RequireAuth>
                  <Suspense fallback={<LazySplash message="Chargement du jeu…" />}>
                    <MotusPage />
                  </Suspense>
                </RequireAuth>
              }
            />
            {/* PUBLIQUE (ADR 0013) : Griffonne se joue sans compte (identité invitée
                côté serveur). Le lobby et la partie partagent le même chunk lazy. */}
            <Route
              path="/griffonne"
              element={
                <Suspense fallback={<LazySplash message="Chargement du jeu…" />}>
                  <GriffonnePage />
                </Suspense>
              }
            />
            <Route
              path="/griffonne/m/:matchId"
              element={
                <Suspense fallback={<LazySplash message="Chargement du jeu…" />}>
                  <GriffonnePage />
                </Suspense>
              }
            />
            {/* Lien de partage par CODE : suit la lignée des revanches (le code est
                transféré à chaque « Rejouer »), puis redirige vers /griffonne/m/:id. */}
            <Route
              path="/griffonne/j/:code"
              element={
                <Suspense fallback={<LazySplash message="Chargement du jeu…" />}>
                  <GriffonneJoinByCode />
                </Suspense>
              }
            />
            {/* Panda Panic (préversion 3D) : route enregistrée SEULEMENT flag on. */}
            {isPandaPanicEnabled() && (
              <Route
                path="/panda-panic"
                element={
                  <ErrorBoundary context="panda-panic:preview">
                    <Suspense fallback={<LazySplash message="Chargement du jeu…" />}>
                      <PandaPanicArea mode="preview" />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
            )}
            {/* Panda Panic en ligne : aire multijoueur, enregistrée SEULEMENT si
                le flag online est on (serveur autoritaire déployé). Même chunk. */}
            {isPandaPanicOnlineEnabled() && (
              <Route
                path="/panda-panic/online/*"
                element={
                  <ErrorBoundary context="panda-panic:online">
                    <Suspense fallback={<LazySplash message="Chargement du jeu…" />}>
                      <PandaPanicArea mode="online" />
                    </Suspense>
                  </ErrorBoundary>
                }
              />
            )}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </PageTransition>
      </div>
    </div>
  );
}
